import { useCallback, useMemo, useState } from "react";
import {
  ButtonDS,
  DownloadOutlined,
  RemoveRedEyeOutlined,
  SidePanelDS,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { isThisMonth } from "date-fns";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { selectManager } from "../../../homeSlice";
import {
  createHomeChargingSupportingDocumentsAsync,
  selectCreateHomeChargingSupportingDocumentsStatus,
  selectHomeChargingSupportingDocuments,
  selectHomeChargingSupportingDocumentsPeriod,
} from "../../homeChargingSupportingDocumentsSlice";
import { getHomeChargingSupportingDocumentStatus } from "../../lib";
import { SupportingDocumentChargesModal } from "../../modals/supportingDocumentChargesModal";
import { SidePanelHeader } from "./sidePanelHeader";
import { SidePanelSectionBlock } from "./sectionBlock";
import { SupportingDocumentDetails } from "./supportingDocumentDetails";
import "../../../../../i18n/i18n";

export const SupportingDocumentSidePanel = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const firstPath = location.pathname.split("/")[1];
  const supportingDocuments = useAppSelector(
    selectHomeChargingSupportingDocuments,
  );
  const supportingDocumentsPeriod = useAppSelector(
    selectHomeChargingSupportingDocumentsPeriod,
  );
  const createStatus = useAppSelector(
    selectCreateHomeChargingSupportingDocumentsStatus,
  );
  const manager = useAppSelector(selectManager);
  const [isChargesModalOpen, setIsChargesModalOpen] = useState(false);

  const isOwnerOrAdmin = useMemo(
    () => manager?.role === "OWNER" || manager?.role === "ADMIN",
    [manager],
  );

  const supportingDocument =
    supportingDocuments.find(
      (supportingDocument) => supportingDocument.driverUuid === params.id,
    ) ?? null;

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/electric_charging/supporting_documents`);
  }, [firstPath, navigate]);

  if (!supportingDocument || !supportingDocumentsPeriod) {
    return;
  }

  const status = getHomeChargingSupportingDocumentStatus(
    supportingDocument,
    isThisMonth(new Date(supportingDocumentsPeriod.from)),
  );

  const canCreateSupportingDocument =
    isOwnerOrAdmin && (status === "TO_CREATE" || status === "CURRENT_MONTH");

  const isCreateSupportingDocumentDisabled =
    status === "CURRENT_MONTH" || createStatus === "processing";

  const canDownloadSupportingDocument = isOwnerOrAdmin && status === "CREATED";

  return (
    <SidePanelDS.SidePanel
      visible={!!supportingDocument}
      onClose={onSidePanelClosed}
      absoluteSection={
        canCreateSupportingDocument ? (
          <>
            <StyledText>
              <TextCapitalized>
                {t("home_charging_supporting_documents.sidePanel.create.text")}
              </TextCapitalized>
            </StyledText>
            <Spacer y={1} />
            <ButtonDS
              text={t(
                "home_charging_supporting_documents.sidePanel.create.button",
              )}
              format="fill"
              buttonType="primary"
              onClick={
                isCreateSupportingDocumentDisabled
                  ? undefined
                  : () =>
                      void dispatch(
                        createHomeChargingSupportingDocumentsAsync({
                          drivers: [{ uuid: supportingDocument.driverUuid }],
                          periodStart: supportingDocumentsPeriod.from,
                          periodEnd: supportingDocumentsPeriod.to,
                        }),
                      )
              }
              disabled={isCreateSupportingDocumentDisabled}
            />
          </>
        ) : canDownloadSupportingDocument ? (
          <ButtonDS
            text={t("home_charging_supporting_documents.sidePanel.download")}
            format="fill"
            buttonType="primary"
            leftIcon={<DownloadOutlined />}
            onClick={() => console.log(supportingDocument.driverUuid)}
          />
        ) : undefined
      }
    >
      <SidePanelDS.Section
        verticalSpacing={1.2}
        horizontalSpacing={1.5}
        background={colors["colors/button/secondary/hover"]}
      >
        <SidePanelHeader
          supportingDocument={supportingDocument}
          status={status}
          period={supportingDocumentsPeriod}
        />
        <Spacer y={1} />
        <SidePanelSectionBlock
          supportingDocument={supportingDocument}
          firstPath={firstPath}
        />
      </SidePanelDS.Section>
      <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
        <SupportingDocumentDetails supportingDocument={supportingDocument} />
      </SidePanelDS.Section>
      <SidePanelDS.Section verticalSpacing={1.5} horizontalSpacing={1.5}>
        <SectionTitle>
          <TextCapitalized>
            {t("home_charging_supporting_documents.sidePanel.charges.title", {
              count: supportingDocument.chargesCount,
            })}
          </TextCapitalized>
        </SectionTitle>
        {supportingDocument.charges.length > 0 && (
          <>
            <Spacer y={2} />
            <ButtonDS
              sizeButton="S"
              format="fill"
              buttonType="secondary"
              text={t(
                "home_charging_supporting_documents.sidePanel.charges.button",
              )}
              leftIcon={<RemoveRedEyeOutlined />}
              onClick={() => setIsChargesModalOpen(true)}
            />
            <SupportingDocumentChargesModal
              visible={isChargesModalOpen}
              onClose={() => setIsChargesModalOpen(false)}
              supportingDocument={supportingDocument}
              period={supportingDocumentsPeriod}
            />
          </>
        )}
      </SidePanelDS.Section>
    </SidePanelDS.SidePanel>
  );
};

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const StyledText = styled.div`
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
`;
